// Showcase media: key art, screenshots and a trailer for a published game.
//
// Two sources, merged. The api carries `media` on every publication
// (migration 0029), which is how any creator's game gets a cover and a
// gallery. The studio's own Originals additionally ship their media inside
// this bundle - the files themselves live under `public/media/<slug>/`,
// captured from the real games by `@sstk/capture-kit`, and
// `public/media/originals.json` indexes them.
//
// The local manifest is a genuine second source rather than a stopgap: this
// media is produced by a build-time pipeline in this repo, versioned with
// the site that renders it, and served from the same origin, so it is
// correct for it to ship with the site. Api media wins where both exist, so
// curating an Original through the api later simply takes over.

import { useEffect, useState } from 'react';

export interface MediaImage {
  url: string;
  alt?: string | null;
}

export interface MediaTrailer {
  url: string;
  poster?: string | null;
  duration_sec?: number | null;
}

export interface GameMedia {
  tagline?: string | null;
  tags: string[];
  cover?: MediaImage | null;
  trailer?: MediaTrailer | null;
  screenshots: MediaImage[];
}

/** The wire shape of `public/media/originals.json`. */
type OriginalsManifest = Record<string, GameMedia>;

let originalsPromise: Promise<OriginalsManifest> | null = null;

/**
 * The bundled Originals media index, fetched once per page load.
 *
 * Failure is not an error state anyone needs to see: a missing or malformed
 * manifest means the showcase falls back to whatever the api knows, which is
 * exactly the behaviour before this file existed.
 */
export function loadOriginalsMedia(): Promise<OriginalsManifest> {
  if (!originalsPromise) {
    originalsPromise = fetch('/media/originals.json')
      .then((res) => (res.ok ? (res.json() as Promise<OriginalsManifest>) : {}))
      .catch(() => ({}));
  }
  return originalsPromise;
}

/** Reset the cached manifest. Tests only. */
export function resetOriginalsMediaCache(): void {
  originalsPromise = null;
}

/**
 * Combine api media with the bundled Originals entry for a slug.
 *
 * Field by field rather than whole-object, so a partially-curated api record
 * (a tagline set through Studio, say, with no gallery yet) still shows the
 * bundled screenshots instead of replacing them with nothing.
 */
export function mergeMedia(apiMedia: GameMedia | null | undefined, local: GameMedia | undefined): GameMedia | null {
  if (!apiMedia && !local) return null;
  const merged: GameMedia = {
    tagline: apiMedia?.tagline ?? local?.tagline ?? null,
    tags: apiMedia?.tags?.length ? apiMedia.tags : (local?.tags ?? []),
    cover: apiMedia?.cover ?? local?.cover ?? null,
    trailer: apiMedia?.trailer ?? local?.trailer ?? null,
    screenshots: apiMedia?.screenshots?.length ? apiMedia.screenshots : (local?.screenshots ?? []),
  };
  return merged;
}

/**
 * Resolve a media URL for rendering.
 *
 * A site-relative path is served by this origin and used as-is. An absolute
 * URL is used as-is too. Nothing else is accepted: these values reach an
 * `src` attribute, and the api's own validation already refuses anything
 * that is not https or site-relative, so a value that fails here came from
 * somewhere it should not have.
 */
export function mediaSrc(url: string | null | undefined): string | null {
  if (!url) return null;
  if (url.startsWith('/') && !url.startsWith('//')) return url;
  if (url.startsWith('https://')) return url;
  return null;
}

/** Poster to show for a trailer before it plays, falling back to the cover. */
export function trailerPoster(media: GameMedia | null): string | null {
  return mediaSrc(media?.trailer?.poster) ?? mediaSrc(media?.cover?.url);
}

/** The image a card or hero should show: the cover, else the first screenshot. */
export function keyArt(media: GameMedia | null): MediaImage | null {
  if (media?.cover) return media.cover;
  return media?.screenshots[0] ?? null;
}

/** `mm:ss` for a trailer length. */
export function formatDuration(seconds: number | null | undefined): string | null {
  if (!seconds || !Number.isFinite(seconds) || seconds <= 0) return null;
  const whole = Math.round(seconds);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
}

/**
 * The bundled media for one Original, or null while it loads (or if the
 * game has none).
 *
 * The marketing pages each need their own game's key art and stills, and
 * they are plain static pages with no api call of their own - reading the
 * same manifest the Showcase reads keeps one source of truth for "what does
 * this game look like" rather than a second hardcoded list of image paths.
 */
export function useOriginalMedia(slug: string): GameMedia | null {
  const [media, setMedia] = useState<GameMedia | null>(null);
  useEffect(() => {
    let cancelled = false;
    void loadOriginalsMedia().then((all) => {
      if (!cancelled) setMedia(all[slug.toLowerCase()] ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [slug]);
  return media;
}
